import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getChatHistory, sendMessageAPI, getIncomingMessages } from '../../../lib/api';
import { connectSocket } from '../../../lib/socket';
import { MediaMessage, isMediaMessage, mediaLabel } from '../components/MediaMessage';
// import { ERROR_CODE_MESSAGES } from '../../../constants/errorCodeMessages';
import './chat.css';

// Simple live chat: enter a set of contacts and switch between them.
const LiveChat = () => {
  const params = useParams();
  const location = useLocation();
  const [contacts, setContacts] = useState([]); // [{wa_id, last_message, timestamp}]
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsHasMore, setContactsHasMore] = useState(true);
  const chatBodyRef = useRef(null);
  const contactsRef = useRef(null);
  const isPrependingRef = useRef(false);
  const currentRef = useRef(null);
  const MSGS_PER_PAGE = 50;
  const inputRef = useRef(null);

  const pickList = (res) => {
    const list = Array.isArray(res)
      ? res
      : (
          res?.chats ||
          res?.messages ||
          res?.data ||
          res?.chat ||
          res?.history ||
          res?.data?.messages ||
          []
        );
    // Many APIs return newest-first; ensure oldest->newest for UI
    if (Array.isArray(list) && list.length > 1) {
      const newerFirst = (list[0]?.timestamp || list[0]?.created_at) > (list[1]?.timestamp || list[1]?.created_at);
      return newerFirst ? [...list].reverse() : list;
    }
    return list;
  };

  const load = async (waId, page = 1, append = false) => {
    if (!waId) return;
    if (!append) setLoading(true);
    else setLoadingOlder(true);
    try {
      const res = await getChatHistory(waId, page, MSGS_PER_PAGE);
      const list = pickList(res);
      if (append) {
        if ((list?.length || 0) === 0) {
          setHasMoreHistory(false);
          return;
        }
        const el = chatBodyRef.current;
        const prevHeight = el ? el.scrollHeight : 0;
        isPrependingRef.current = true;
        setHistory((prev) => [...list, ...prev]);
        setMessagesPage(page);
        setHasMoreHistory(list.length >= MSGS_PER_PAGE);
        // Preserve scroll position after prepending older messages
        setTimeout(() => {
          if (!el) return;
          const newHeight = el.scrollHeight;
          el.scrollTop = newHeight - prevHeight;
          isPrependingRef.current = false;
        }, 50);
      } else {
        setHistory(list);
        setMessagesPage(1);
        setHasMoreHistory(list.length >= MSGS_PER_PAGE);
      }
    } finally { if (!append) setLoading(false); else setLoadingOlder(false); }
  };

  const onSelect = (waId) => {
    setCurrent(waId);
    currentRef.current = waId;
    // Auto-load when a contact is selected
    setMessagesPage(1);
    setHasMoreHistory(true);
    load(waId, 1, false);
  };

  // Simple avatar generator
  const Avatar = ({ id, size = 40 }) => {
    const { bg, fg, txt } = useMemo(() => {
      const s = String(id || '?');
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
      const hue = h % 360;
      const bg = `hsl(${hue} 70% 85%)`;
      const fg = `hsl(${hue} 60% 25%)`;
      const digits = s.replace(/\D+/g, '');
      const txt = digits ? digits.slice(-2) : s.slice(0, 2).toUpperCase();
      return { bg, fg, txt };
    }, [id]);
    return (
      <div
        className="avatar"
        style={{ width: size, height: size, background: bg, color: fg, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
      >
        {txt}
      </div>
    );
  };

  const onSend = async () => {
    if (!current || !msg.trim()) return;
    const body = msg.trim();
    setMsg('');
    let messageId;
    try {
      const res = await sendMessageAPI(current, body);
      messageId = res?.whatsAppResponse?.messages?.[0]?.id || res?.message_id || res?.id;
    } catch (e) {
      // fall through, still show created bubble optimistically
    }
    setHistory((prev) => [
      ...prev,
      {
        message_id: messageId,
        wa_id: current,
        direction: 'outbound',
        message_body: body,
        timestamp: new Date().toISOString(),
        status: 'created',
      },
    ]);
    // Do not reload chat history; socket will update status
  };

  // Auto-polling disabled per request (no periodic refresh)

  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    // Scroll only the chat body container
    if (isPrependingRef.current) return; // we already preserved scroll on prepend
    try { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }
    catch (_) { el.scrollTop = el.scrollHeight; }
  }, [history]);

  // Auto-resize message textarea as user types
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const max = 140; // keep within CSS max-height
    ta.style.height = Math.min(ta.scrollHeight, max) + 'px';
  }, [msg]);

  // Initialize from route param / query (?wa_id=)
  useEffect(() => {
    const routeId = params.waid;
    const searchId = new URLSearchParams(location.search).get('wa_id');
    const initial = routeId || searchId;
    // Prime contacts list
    // Start loading chat immediately for the provided waid (don't wait for contacts)
    if (initial) {
      setCurrent(initial);
      currentRef.current = initial;
      setMessagesPage(1);
      setHasMoreHistory(true);
      load(initial, 1, false);
    }
    // Prime contacts list in background
    fetchContacts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to route param changes (navigate to a different waid)
  useEffect(() => {
    const routeId = params.waid;
    if (!routeId) return;
    setCurrent(routeId);
    currentRef.current = routeId;
    // ensure in contacts
    if (!contacts.find((c) => c.wa_id === routeId)) {
      setContacts((c) => [{ wa_id: routeId, last_message: '', timestamp: new Date().toISOString() }, ...c]);
    }
    // Auto-load on route change
    setMessagesPage(1);
    setHasMoreHistory(true);
    load(routeId, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.waid]);

  // Keep currentRef in sync
  useEffect(() => { currentRef.current = current; }, [current]);

  // Socket: receive new messages and status updates
  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return;

    const onNewMessage = (newMsg) => {
      // Log and normalize timestamp to avoid Invalid Date issues
      console.log('socket:new_message', newMsg);
      const pickTs = (m) => m?.timestamp || m?.message_timestamp || m?.created_at || m?.ts;
      const normalizeTs = (ts) => {
        if (!ts) return null;
        if (ts instanceof Date) return ts.toISOString();
        if (typeof ts === 'number') {
          const ms = ts > 1e12 ? ts : ts * 1000; // seconds -> ms
          const d = new Date(ms);
          return isNaN(d.getTime()) ? null : d.toISOString();
        }
        if (typeof ts === 'string') {
          const s = ts.trim();
          if (/^\d+$/.test(s)) {
            const num = parseInt(s, 10);
            const ms = num > 1e12 ? num : num * 1000;
            const d = new Date(ms);
            return isNaN(d.getTime()) ? null : d.toISOString();
          }
          const d = new Date(s);
          return isNaN(d.getTime()) ? null : d.toISOString();
        }
        return null;
      };

      const ts = normalizeTs(pickTs(newMsg)) || new Date().toISOString();
      const normalized = {
        ...newMsg,
        timestamp: ts,
        message_body: newMsg.message_body ?? newMsg.message_content ?? null,
        message_type: newMsg.message_type || newMsg.type || null,
        direction: newMsg.direction || 'incoming',
      };
      if (!newMsg || !newMsg.wa_id) return;
      const openWaid = currentRef.current;
      if (openWaid && newMsg.wa_id === openWaid) {
        // Append into open chat
        setHistory((prev) => [...prev, normalized]);
      }
      // Update contacts recency
      setContacts((prevContacts) => {
        const idx = prevContacts.findIndex((c) => c.wa_id === newMsg.wa_id);
        const type = newMsg.message_type || newMsg.type;
        const preview =
          newMsg.message_content ||
          newMsg.message_body ||
          (type ? `[${mediaLabel(type)}]` : '');
        const updated = {
          wa_id: newMsg.wa_id,
          last_message: preview,
          timestamp: ts,
        };
        if (idx !== -1) {
          const copy = [...prevContacts];
          copy.splice(idx, 1);
          return [updated, ...copy];
        }
        return [updated, ...prevContacts];
      });
    };

    const onUpdateStatus = (data) => {
      if (!data || !data.message_id) return;
      setHistory((prev) => prev.map((m) => (m.message_id === data.message_id ? { ...m, status: data.status, errors: data.errorsString || m.errors } : m)));
    };

    socket.on('new_message', onNewMessage);
    socket.on('update_message_status', onUpdateStatus);

    return () => {
      try {
        socket.off('new_message', onNewMessage);
        socket.off('update_message_status', onUpdateStatus);
        // Do not disconnect here; socket is shared app-wide via MainLayout
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load older messages when scrolled to the top
  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop <= 10 && hasMoreHistory && !loadingOlder && current) {
        const next = messagesPage + 1;
        load(current, next, true);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, messagesPage, hasMoreHistory, loadingOlder]);

  // Infinite scroll for contacts list (load more on reaching near bottom)
  useEffect(() => {
    const el = contactsRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
      if (nearBottom && contactsHasMore && !contactsLoading) {
        fetchContacts(contactsPage + 1, false);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsHasMore, contactsLoading, contactsPage]);

  // Fetch contacts from incoming messages and build a recents list
  const fetchContacts = async (page = 1, reset = false) => {
    if (contactsLoading) return;
    setContactsLoading(true);
    try {
      const res = await getIncomingMessages(page, 30);
      const msgs = res?.messages || res?.data || [];
      const map = new Map();
      const seed = reset ? [] : contacts;
      seed.forEach((c) => map.set(c.wa_id, c));
      msgs.forEach((m) => {
        const id = m.wa_id || m.recipient_id;
        if (!id) return;
        const prev = map.get(id);
        const ts = m.timestamp || m.message_timestamp || m.created_at;
        const type = m.message_type || m.type;
        const body = m.message_body || m.body || '';
        const preview = body || (type && type !== 'text' ? `[${mediaLabel(type)}]` : '');
        const rec = { wa_id: id, last_message: preview, timestamp: ts };
        if (!prev || (prev.timestamp || '') < (ts || '')) map.set(id, rec);
      });
      const merged = Array.from(map.values()).sort((a,b)=> new Date(b.timestamp||0)-new Date(a.timestamp||0));
      setContacts(merged);
      setContactsHasMore((msgs.length || 0) >= 20);
      setContactsPage(page);
    } finally { setContactsLoading(false); }
  };

  // Helpers for time/date formatting and grouping
  const parseTs = (ts) => {
    if (!ts) return null;
    if (ts instanceof Date) return isNaN(ts.getTime()) ? null : ts;
    if (typeof ts === 'number') {
      const ms = ts > 1e12 ? ts : ts * 1000;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof ts === 'string') {
      const s = ts.trim();
      if (/^\d+$/.test(s)) {
        const num = parseInt(s, 10);
        const ms = num > 1e12 ? num : num * 1000;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const formatClock = (ts) => {
    const d = parseTs(ts);
    if (!d) return '';
    try { return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); }
    catch { return d.toISOString().slice(11,16); }
  };
  const dayKey = (ts) => {
    const d = parseTs(ts);
    return d ? d.toDateString() : '';
  };
  const dayLabel = (ts) => {
    const d = parseTs(ts); const now = new Date();
    if (!d) return '';
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const daysDiff = Math.round((startOfToday - startOfThat) / 86400000);
    if (daysDiff === 0) return 'Today';
    if (daysDiff === 1) return 'Yesterday';
    // Within last 6 days -> weekday name (e.g., Friday)
    if (daysDiff > 1 && daysDiff < 7) {
      return d.toLocaleDateString('en-US', { weekday: 'long' });
    }
    // Else -> Mon, Aug 25 (add year only if different)
    const base = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (d.getFullYear() !== now.getFullYear()) return `${base}, ${d.getFullYear()}`;
    return base;
  };
  const formatListTs = (ts) => {
    const d = parseTs(ts); const now = new Date();
    if (!d) return '';
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const daysDiff = Math.round((startOfToday - startOfThat) / 86400000);
    if (daysDiff === 0) return formatClock(ts);
    if (daysDiff === 1) return 'Yesterday';
    if (daysDiff > 1 && daysDiff < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="livechat-wrap">
      <aside className="contacts">
        <div className="contacts-header">
          <h3>Live Chat</h3>
        </div>
        <div className="contacts-list" ref={contactsRef}>
          {contactsLoading && contacts.length === 0 && (
            Array.from({length:8}).map((_,i)=> (
              <div key={`sk-${i}`} className="contact-item sk" style={{ height: 56 }} />
            ))
          )}
          {contacts.length === 0 && !contactsLoading && <div className="muted">No contacts</div>}
          {contacts.map((c) => (
            <button key={c.wa_id} className={`contact-item ${current === c.wa_id ? 'active' : ''}`} onClick={()=>onSelect(c.wa_id)}>
              <div className="contact-row">
                <Avatar id={c.wa_id} size={44} />
                <div className="contact-main">
                  <div className="line1">
                    <div className="waid">{c.wa_id}</div>
                    <div className="ts">{c.timestamp ? formatListTs(c.timestamp) : ''}</div>
                  </div>
                  <div className="line2 last">{c.last_message?.length > 48 ? c.last_message.slice(0,48)+"…" : (c.last_message || 'No message')}</div>
                </div>
              </div>
            </button>
          ))}
          {/* infinite scroll for contacts: load more on scroll */}
        </div>
      </aside>
      <section className="chat-section">
        <div className="chat-header">
          {current ? (
            <>
              <Avatar id={current} size={36} />
              <div className="title">{current}</div>
            </>
          ) : (
            <div className="title">No contact selected</div>
          )}
        </div>
        <div className="chat-body-pane" ref={chatBodyRef}>
          {loadingOlder && (
            <div className="muted" style={{ textAlign: 'center', padding: '6px 0' }}>Loading older…</div>
          )}
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => {
              const me = i % 2 === 1;
              const widths = [45, 52, 60, 40, 65, 48, 58, 50];
              const w = widths[i % widths.length];
              return (
                <div
                  key={`sk-${i}`}
                  className={`bubble sk ${me ? 'me' : 'them'}`}
                  style={{ height: 40, borderRadius: 12, marginBottom: 8, width: `${w}%` }}
                />
              );
            })
          ) : (
            history.map((m,i) => {
              const dir = (m.direction || m.dir || '').toLowerCase();
              const me = dir === 'outbound' || dir === 'outgoing';
              let content;
              const caption = m.message_content || m.message_body || m.body || m.text || '';
              if (isMediaMessage(m)) {
                content = (
                  <MediaMessage
                    mediaId={m.media_id}
                    messageType={m.message_type || m.type}
                    mimeType={m.mime_type}
                    caption={typeof caption === 'string' ? caption : ''}
                    filename={m.filename || m.file_name}
                  />
                );
              } else {
                try {
                  const mb = m.message_body || m.body || m.text;
                  if (me && typeof mb === 'string') {
                    const parsed = JSON.parse(mb);
                    if (parsed?.type === 'template') {
                      content = (
                        <div className="tpl-bubble">
                          <div className="tpl-body">Template</div>
                        </div>
                      );
                    }
                  }
                } catch {}
              }
              const status = (m.status || '').toLowerCase();
              const statusEl = status === 'read' ? <span className="ticks read">✓✓</span>
                : status === 'delivered' ? <span className="ticks">✓✓</span>
                : status === 'sent' ? <span className="ticks">✓</span>
                : status === 'created' ? <span className="clock">🕒</span>
                : status === 'failed' ? <span className="failed">✖</span>
                : null;
              const prev = history[i-1];
              const prevDay = prev ? dayKey(prev.timestamp) : '';
              const curDay = dayKey(m.timestamp);
              const showDayBreak = i === 0 || prevDay !== curDay;
              return (
                <React.Fragment key={i}>
                  {showDayBreak && (
                    <div className="day-sep">{dayLabel(m.timestamp)}</div>
                  )}
                  <div className={`bubble ${me ? 'me' : 'them'}`}>
                    <div className="text">{content || caption || ''}</div>
                    <div className="meta">
                      <span className="ts">{m.timestamp ? formatClock(m.timestamp) : ''}</span>
                      {statusEl}
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          {/* bottom spacer for padding */}
          <div style={{ height: 1 }} />
        </div>
        <div className="chat-input">
          <textarea
            ref={inputRef}
            className="msg-input"
            placeholder="Type a message"
            value={msg}
            onChange={(e)=>setMsg(e.target.value)}
            onKeyDown={(e)=>{
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
            }}
            rows={1}
          />
          <button className="send-btn" onClick={onSend} disabled={!msg.trim()} aria-label="Send">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12L21 3L14 21L11 13L3 12Z" stroke="currentColor" strokeWidth="2" fill="currentColor" />
            </svg>
          </button>
        </div>
      </section>
    </div>
  );
};

export default LiveChat;
